<template>
  <div v-if="open" class="np">
    <div class="np__head">
      <span class="np__title">{{ $tut('tut.pathTitle') }}</span>
      <span class="np__count">{{ doneCount }}/{{ rows.length }}</span>
    </div>
    <div class="np__bar"><div class="np__bar-fill" :style="{ width: barWidth }"></div></div>

    <div class="np__list">
      <div
        v-for="r in rows" :key="r.code"
        class="np__row"
        :class="{ 'np__row--done': r.claimed, 'np__row--ready': r.ready, 'np__row--help': canHelp(r) }"
        :title="canHelp(r) ? $tut('tut.pathHelpHint') : ''"
        @click="help(r)"
      >
        <span class="np__ico">{{ ICONS[r.code] || '•' }}</span>
        <span class="np__name">{{ $tut('tut.path.' + r.code) }}</span>
        <span class="np__rew">
          <span v-for="c in rewardChips(r.reward)" :key="c.key" class="np__chip">{{ c.icon }}{{ c.value }}</span>
        </span>
        <button v-if="r.ready" class="np__btn" :disabled="busy" @click.stop="claim(r.code)">{{ $tut('tut.pathClaim') }}</button>
        <span v-else-if="r.claimed" class="np__ok">✓</span>
        <span v-else class="np__progress">{{ r.progress }}/{{ r.goal }}</span>
      </div>
    </div>

    <div v-if="got.length" class="np__got">
      {{ $tut('tut.pathGot') }}
      <span v-for="(g, i) in got" :key="i" class="np__chip np__chip--got">{{ grantText(g) }}</span>
    </div>
    <div v-if="msg" class="np__msg">{{ msg }}</div>
  </div>
</template>

<script>
/**
 * Чек-лист «Путь новичка» (BackEnd/docs/TUTORIAL_PLAN.md §5) — виджет в профиле.
 *
 * Подхватывает игрока там, где кончились подсказки обучения: 7 пунктов с нарастающими наградами.
 * Прогресс считает СЕРВЕР (хуки в бою/Pack/рынке), награду выдаёт тоже сервер — клиент лишь
 * показывает состояние и жмёт «Забрать».
 */
import { serverError } from '@/i18n/serverErrors';
import { HELP_SPOTS } from '@/components/tutorial/TutorialHelp';

const ICONS = {
  win_pvp: '⚔️', equip_5: '🛡️', heal_unit: '💚', disassemble: '🔨',
  daily_chest: '🎁', market_sell: '🏷️', invite_friend: '🤝',
};
// те же значки ресурсов, что на экране заданий дня
const RES_ICONS = {
  energy: '⚡', tickets: '🎟️', gems_small: '💎', materials: '🪵',
  boost_minutes: '⏳', scroll_apprentice: '📜', scroll_master: '📕', ether: '🧊', hammer: '🔨',
};

export default {
  name: 'NewbiePath',
  data() {
    return { ICONS, busy: false, got: [], msg: '' };
  },
  computed: {
    rows() { return this.$store.getters.tutorialChecklist; },
    open() { return this.$store.getters.tutorialChecklistOpen && this.rows.length > 0; },
    doneCount() { return this.rows.filter((r) => r.claimed).length; },
    barWidth() { return `${this.rows.length ? Math.round((this.doneCount / this.rows.length) * 100) : 0}%`; },
  },
  methods: {
    // Пункт можно «показать»: клик уводит в нужный раздел, там наставник объясняет, что нажать
    // (решение оунера 2026-07-30). Забранные пункты не ведут никуда.
    canHelp(row) { return !row.claimed && !!HELP_SPOTS[row.code]; },
    help(row) {
      if (!this.canHelp(row)) return;
      this.$store.dispatch('tutorialHelp', row.code);
      const route = HELP_SPOTS[row.code].route;
      if (this.$route.path !== route) this.$router.push(route);
    },
    rewardChips(reward) {
      const out = [];
      Object.keys(RES_ICONS).forEach((key) => {
        const value = reward && reward[key];
        if (value) out.push({ key, icon: RES_ICONS[key], value });
      });
      if (reward && reward.ammo) out.push({ key: 'ammo', icon: '🗡️', value: 1 });
      return out;
    },
    // строка выданного: сервер шлёт те же `granted`, что и задания дня
    grantText(g) {
      if (!g || !g.kind) return '';
      if (g.kind === 'ammo') return `🗡️ ${this.$t('daily.res.ammo')}`;
      return `${RES_ICONS[g.kind] || '•'}${g.amount != null ? g.amount : ''}`;
    },
    async claim(code) {
      if (this.busy) return;
      this.busy = true;
      this.msg = '';
      try {
        this.got = await this.$store.dispatch('tutorialClaim', code);
      } catch (e) {
        const detail = e && e.response && e.response.data && e.response.data.detail;
        this.msg = serverError(detail) || this.$t('pack.error');
        this.$store.dispatch('fetchTutorial'); // состояние могло уйти вперёд (награду взяли с другого устройства)
      } finally { this.busy = false; }
    },
  },
};
</script>

<style scoped lang="scss">
.np {
  margin-top: 10px;
  border-radius: 12px;
  padding: 9px 11px;
  border: 1px solid rgba(255, 200, 90, 0.35);
  background: linear-gradient(140deg, rgba(60, 42, 30, 0.55), rgba(30, 20, 44, 0.6));
  font-size: 0.9em;
  text-align: left; // профиль центрирует текст — список пунктов читается только слева

  &__head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  &__title { font-weight: 700; color: #ffcf94; }
  &__count { font-size: 0.85em; color: #b3a5d8; }

  &__bar {
    height: 5px; border-radius: 4px; margin: 6px 0 8px;
    background: rgba(255, 255, 255, 0.12); overflow: hidden;
  }
  &__bar-fill { height: 100%; background: linear-gradient(90deg, #ffd257, #f0a13a); transition: width 0.3s; }

  &__list { display: flex; flex-direction: column; gap: 4px; }
  &__row {
    display: flex; align-items: center; gap: 6px; min-height: 22px;
    &--done { opacity: 0.5; }
    &--ready .np__name { color: #ffe27a; }
    &--help { cursor: pointer; }
    &--help:hover .np__name { color: #ffcf94; text-decoration: underline; }
  }
  &__ico { width: 1.2em; flex: none; text-align: center; }
  &__name { flex: 1; min-width: 0; color: #ded3f5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__rew { display: flex; gap: 3px; flex: none; }
  &__chip {
    font-size: 0.8em; border-radius: 8px; padding: 1px 5px;
    background: rgba(255, 255, 255, 0.08); color: #f6ecd2; white-space: nowrap;
    &--got { background: rgba(255, 210, 120, 0.22); }
  }
  &__progress { font-size: 0.8em; color: #b3a5d8; flex: none; min-width: 2.4em; text-align: right; }
  &__ok { color: #7ad07a; flex: none; min-width: 2.4em; text-align: right; }
  &__btn {
    font-family: inherit; font-size: 0.8em; border-radius: 9px; padding: 3px 10px; cursor: pointer; flex: none;
    border: 1px solid rgba(255, 200, 90, 0.6); color: #3a2500; font-weight: 700;
    background: linear-gradient(135deg, #ffd257, #f0a13a);
    &:disabled { opacity: 0.6; cursor: default; }
  }
  &__got { margin-top: 7px; display: flex; flex-wrap: wrap; gap: 4px; align-items: center; font-size: 0.85em; color: #cdbff0; }
  &__msg { margin-top: 6px; font-size: 0.85em; color: #ffb3b3; }
}
</style>
